import React from 'react';
import {
  oneOfType,
  shape,
  string,
  bool,
  func,
} from 'prop-types';

import problemActivityShape from '../../../../../shapes/problem-activity';
import updateStatusActivityShape from '../../../../../shapes/status-update-activity';
import { TRACKING_EVENT_TYPES } from '../../../../../constants';
import ActivityStatusUpdated from '../activity-status-updated';
import ActivityLocationUpdate from '../activity-location-update';
import ActivityLogProblem from '../activity-log-problem';
import ActivityProblemResolved from '../activity-problem-resolved';
import trackingEventShape from '../../../../../shapes/tracking-event-activity';
import connectionStatusActivityShape from '../../../../../shapes/connection-status-activity';
import ActivityConnectionStatusUpdated from '../activity-connection-status-updated/activity-connection-status-updated';
import NoContent from '../../card-shared-components/card-no-content';
import ActivityTmsNote from '../activity-tms-note/activity-tms-note';
import ActivityTmsTrack from '../activity-tms-track/activity-tms-track';
import ActivityBounce from '../activity-bounce';
import ActivityRebooked from '../activity-rebooked';

/**
 * Activity component
 * @param {object} params - Object containing props
 * @param {object} params.activity - Activity data to show
 * @param {object} params.options - Options for component
 * @param {func} params.scrollToActivity - Function handler for scrolling to this activity in the parent
 * @param {boolean} params.hideComments - boolean value that determines if the comments should be hidden
 * @return {element}
 */
const Activity = ({
  activity,
  options,
  scrollToActivity,
  hideComments,
}) => {
  /**
     * Conditionally renders the correct activity type
     * based on the lastUpdated's eventType
     * @return {element}
     */
  const renderActivity = () => {
    if (!activity) {
      return (
        <NoContent message={options.noContentMessage} />
      );
    }
    switch (activity.eventType) {
      case TRACKING_EVENT_TYPES.statusUpdate:
        return <ActivityStatusUpdated activity={activity} />;
      case TRACKING_EVENT_TYPES.manualTrack:
        return <ActivityLocationUpdate lastTrackingEvent={activity} />;
      case TRACKING_EVENT_TYPES.automaticTrack:
        return (
          <ActivityLocationUpdate
            lastTrackingEvent={activity}
            visibilityAppName={options?.visibilityAppName}
          />
        );
      case TRACKING_EVENT_TYPES.problemSet:
        return (
          <ActivityLogProblem
            activity={activity}
            errorBackground={options?.errorBackgroundForProblems}
            scrollTo={scrollToActivity}
            hideComments={hideComments}
          />
        );
      case TRACKING_EVENT_TYPES.problemResolution:
        return <ActivityProblemResolved activity={activity} />;
      case TRACKING_EVENT_TYPES.visibilityAppStatusUpdate:
        return <ActivityConnectionStatusUpdated activity={activity} />;
      case TRACKING_EVENT_TYPES.tmsInternalNote:
        return <ActivityTmsNote activity={activity} />;
      case TRACKING_EVENT_TYPES.tmsInternalTrack:
        return <ActivityTmsTrack activity={activity} />;
      case TRACKING_EVENT_TYPES.bounce:
        return <ActivityBounce activity={activity} />;
      case TRACKING_EVENT_TYPES.rebooked:
        return <ActivityRebooked activity={activity} />;
      default:
        return <p>Tracking event type not recognized</p>;
    }
  };

  // Render the activity
  return renderActivity();
};

Activity.propTypes = {
  activity: oneOfType([
    problemActivityShape,
    trackingEventShape,
    updateStatusActivityShape,
    connectionStatusActivityShape,
  ]),
  options: shape({
    visibilityAppName: string,
    errorBackgroundForProblems: bool,
    noContentMessage: string,
  }),
  scrollToActivity: func,
  hideComments: bool,
};

Activity.defaultProps = {
  activity: {},
  options: { noContentMessage: 'No action taken on this load.' },
  scrollToActivity: () => { },
  hideComments: false,
};

export default Activity;
